import { EditIcon } from '@/assets/EditIcon';
import {
  GrainUsageTypeEnum,
  ROUTE_PARTIES_GRAIN_EDIT,
} from '@/common/constants';
import { RemoveIcon } from '@/common/icons/RemoveIcon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IParty } from '@/features/parties-grain/models';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const createColumns = (): ColumnDef<IParty>[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deletePartyGrain, fetchPartiesGrain, storageTypesByGoods } =
    usePartyGrainStore();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('activeTab');
  const pageSize = Number(searchParams.get('size')) || 10;
  const currentPage = Number(searchParams.get('page')) || 1;

  const deleteItem = async (id: string) => {
    try {
      await deletePartyGrain(id);
      fetchPartiesGrain({
        page: currentPage,
        size: pageSize,
        usage_type: activeTab as GrainUsageTypeEnum,
      });
    } catch (error) {
      console.error('Error deleting party:', error);
    }
  };

  return useMemo(
    () => [
      {
        accessorKey: 'place_number',
        header: () => t('place.number'),
      },
      {
        accessorKey: 'place_name',
        header: () => 'Joy nomi',
        cell: ({ row }) => row.original?.place_name || '-',
      },
      {
        accessorKey: 'crop_type_name',
        header: () => t('selection.type'),
      },
      {
        accessorKey: 'plant_name',
        header: () => 'Ekin turi',
        cell: ({ row }) => row.original?.plant_name || '-',
      },
      {
        accessorKey: 'storage_type',
        header: 'Saqlash turi',
        cell: ({ row }) =>
          storageTypesByGoods?.find(
            (item) => item.value === String(row.original?.storage_type),
          )?.label || '-',
      },
      {
        accessorKey: 'class',
        header: () => t('class'),
      },
      {
        accessorKey: 'capacity',
        header: () => t('capacity'),
      },
      {
        accessorKey: 'transit_in_loaded',
        header: () => "Yuklangan g'alla",
        cell: ({ row }) =>
          Number(row.original?.receptions_loaded)?.toFixed(1) || '-',
      },
      {
        accessorKey: 'free_space',
        header: () => t('free.space'),
        cell: ({ row }) => Number(row.original?.free_space)?.toFixed(1) || '-',
      },
      {
        id: 'action',
        header: () => <div className="text-cente">{t('action')}</div>,
        cell: ({ row }) => (
          <div className="flex-center-center">
            <Button
              size="icon"
              variant="ghost"
              className="size-8 !rounded-10"
              onClick={() =>
                navigate(
                  ROUTE_PARTIES_GRAIN_EDIT +
                    '/' +
                    row.original.id +
                    '?activeTab=' +
                    activeTab,
                )
              }
            >
              <EditIcon />
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8 !rounded-10"
                >
                  <RemoveIcon />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t('confirm.delete')}</DialogTitle>
                </DialogHeader>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">{t('cancel')}</Button>
                  </DialogClose>
                  <Button onClick={() => deleteItem(row.original.id)}>
                    {t('yes')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        ),
      },
    ],
    [t, deleteItem],
  );
};
